/** People this source brought, and the latest arrivals (visits / signups) from it. */
import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Footprints, UsersRound } from "lucide-react";
import { Avatar, Button } from "@/components/ui";
import { cn } from "@/lib/utils";
import { Empty, Pill, Section, Segmented, dayLabel, num, timeAgo, usd } from "@/features/command/ui";
import { clickIdLabel, hostOf, howLabel, personName } from "../shared";
import type { SourcePerson, Touch } from "../types";

const PLAN: Record<string, string> = { free: "Free", pro: "Pro", agency: "Agency", mobileapp: "Mobile app" };

function planLabel(p: SourcePerson): { text: string; color: string | null } {
  if (!p.account_id) return { text: "No account yet", color: null };
  const plan = PLAN[p.plan_code ?? ""] ?? (p.plan_code ? p.plan_code : "Free");
  if (p.subscription_status === "trialing") return { text: `${plan} trial`, color: "amber" };
  if (["past_due", "unpaid", "incomplete"].includes(p.subscription_status ?? "")) return { text: `${plan}, payment problem`, color: "rose" };
  if (p.plan_code === "pro" || p.plan_code === "agency") return { text: plan, color: "indigo" };
  return { text: plan, color: null };
}

const paid = (p: SourcePerson) => (Number(p.revenue_usd) || 0) + (Number(p.lifetime_spend_usd) || 0);

export function PeopleSection({ people, sourceId, from }: { people: SourcePerson[]; sourceId: string; from: string }) {
  const [all, setAll] = useState(false);
  const [order, setOrder] = useState<"new" | "paid">("new");
  const sorted = useMemo(() => (order === "paid" ? [...people].sort((a, b) => paid(b) - paid(a)) : people), [people, order]);
  const stats = useMemo(() => ({
    accounts: people.filter((p) => p.account_id).length,
    paying: people.filter((p) => paid(p) > 0).length,
    total: people.reduce((a, p) => a + paid(p), 0),
  }), [people]);
  const shown = all ? sorted : sorted.slice(0, 8);

  return (
    <Section
      title="People who came from here"
      icon={UsersRound}
      count={people.length}
      action={<Link to={`/contacts?source=${sourceId}`} state={{ from }} className="shrink-0 whitespace-nowrap text-xs font-medium text-indigo-300 hover:text-indigo-200">See all<span className="hidden sm:inline"> in People</span></Link>}
      bodyClassName="p-0"
    >
      {people.length === 0 ? (
        <Empty icon={UsersRound} title="Nobody credited here yet">
          When someone signs up through this source, they show up here with what they have paid.
        </Empty>
      ) : (
        <>
          <div className="grid grid-cols-3 divide-x divide-zinc-800/70 border-b border-zinc-800/70 text-center">
            <Mini label="People" value={people.length >= 200 ? "200+" : num(people.length)} />
            <Mini label="Have an account" value={num(stats.accounts)} />
            <Mini label="Paid so far" value={usd(stats.total)} accent />
          </div>
          {people.length > 1 && (
            <div className="flex items-center justify-between gap-2 border-b border-zinc-800/70 px-4 py-2">
              <span className="text-[11px] text-zinc-500">{stats.paying ? `${num(stats.paying)} of them have paid` : "Nobody has paid yet"}</span>
              <Segmented size="sm" value={order} onChange={setOrder} options={[{ value: "new", label: "Newest" }, { value: "paid", label: "Paid most" }]} />
            </div>
          )}
          <ul className="divide-y divide-zinc-800/60">
            {shown.map((p) => {
              const plan = planLabel(p);
              const name = personName(p);
              const money = paid(p);
              return (
                <li key={p.id} className="grid grid-cols-[auto_minmax(0,1fr)_auto] items-center gap-x-3 gap-y-1 px-4 py-2.5 sm:grid-cols-[auto_minmax(0,1.5fr)_minmax(0,1fr)_auto_auto]">
                  <Avatar name={name} email={p.email} size="sm" />
                  <div className="min-w-0">
                    <Link to={`/contacts/${p.id}`} state={{ from }} className="block truncate text-sm font-medium text-zinc-100 hover:text-indigo-200">{name}</Link>
                    <div className="truncate text-[11px] text-zinc-500" title={p.last_how ?? undefined}>{howLabel(p)}</div>
                  </div>
                  <div className="hidden min-w-0 sm:block">
                    {p.account_id ? (
                      <Link to={`/accounts/${p.account_id}`} state={{ from }} className="block truncate text-xs text-zinc-300 hover:text-indigo-200">{p.account_name || "Account"}</Link>
                    ) : null}
                    <Pill color={plan.color} className="mt-0.5">{plan.text}</Pill>
                  </div>
                  <div className={cn("text-right text-sm font-semibold tabular-nums", money > 0 ? "text-emerald-300" : "text-zinc-500")}>{usd(money)}</div>
                  <div className="col-span-3 -mt-0.5 flex items-center gap-2 pl-11 text-[11px] text-zinc-500 sm:col-span-1 sm:mt-0 sm:block sm:pl-0 sm:text-right">
                    <span className="sm:hidden"><Pill color={plan.color}>{plan.text}</Pill></span>
                    <span title="Signed up">{dayLabel(p.platform_created_at ?? p.created_at, { month: "short", day: "numeric", year: "2-digit" })}</span>
                  </div>
                </li>
              );
            })}
          </ul>
          {people.length > 8 && (
            <div className="border-t border-zinc-800/70 px-4 py-2.5 text-center">
              <Button variant="ghost" size="sm" onClick={() => setAll((a) => !a)}>
                {all ? "Show fewer" : `Show all ${num(people.length)}`}
              </Button>
            </div>
          )}
        </>
      )}
    </Section>
  );
}

function Mini({ label, value, accent }: { label: string; value: string; accent?: boolean }) {
  return (
    <div className="px-2 py-3">
      <div className={cn("text-lg font-semibold tabular-nums", accent ? "text-emerald-300" : "text-zinc-100")}>{value}</div>
      <div className="text-[11px] text-zinc-500">{label}</div>
    </div>
  );
}

/** "Clicked a Google ad" / "Tagged link: ig / social" / "From instagram.com" / "Typed it in". */
export function touchHow(t: Touch): string {
  const ids = Object.keys(t.click_ids ?? {});
  const paidIds = ids.filter((k) => k !== "fbclid");
  if (paidIds.length) return `Clicked a ${[...new Set(paidIds.map(clickIdLabel))].join(" / ")} ad`;
  const tags = [t.utm_source, t.utm_medium, t.utm_campaign].filter(Boolean);
  if (tags.length) return `Tagged link: ${tags.join(" / ")}`;
  const host = hostOf(t.referrer);
  if (host) return `From ${host}`;
  if (ids.includes("fbclid")) return "Clicked a link on Facebook or Instagram";
  return "No source on the visit";
}

const KIND: Record<string, string> = { signup: "Signed up", visit: "Visited", lead: "Became a lead", form: "Filled a form" };

export function ArrivalsSection({ touches, from }: { touches: Touch[]; from: string }) {
  // The site and the platform both record a signup a few seconds apart (one
  // of them as the "identity" row); show it once. Page views are noise here.
  const rows = useMemo(() => {
    const out: Array<Touch & { how: string }> = [];
    for (const t of touches) {
      if (t.kind === "pageview") continue;
      const kind = t.kind === "identity" ? "signup" : t.kind;
      const how = touchHow(t);
      const dup = out.find((o) => o.contact_id === t.contact_id && o.kind === kind && Math.abs(Date.parse(o.ts) - Date.parse(t.ts)) < 10 * 60_000);
      if (!dup) out.push({ ...t, kind, how });
    }
    return out;
  }, [touches]);

  return (
    <Section title="Recent arrivals" icon={Footprints} count={rows.length} bodyClassName="p-0">
      {rows.length === 0 ? (
        <Empty icon={Footprints} title="No arrivals recorded yet">Visits and signups from this source show up here as they happen.</Empty>
      ) : (
        <ul className="divide-y divide-zinc-800/60">
          {rows.map((t, i) => {
            const path = (t.landing_page ?? "").split("?")[0] || null;
            return (
              <li key={`${t.contact_id}-${t.ts}-${i}`} className="flex items-start gap-3 px-4 py-2.5">
                <span className={cn("mt-1.5 h-2 w-2 shrink-0 rounded-full", t.kind === "signup" ? "bg-emerald-400" : "bg-indigo-400")} />
                <div className="min-w-0 flex-1">
                  <div className="truncate text-sm text-zinc-200">
                    <Link to={`/contacts/${t.contact_id}`} state={{ from }} className="font-medium text-zinc-100 hover:text-indigo-200">{t.contact_name || "Someone"}</Link>
                    <span className="text-zinc-500"> {(KIND[t.kind ?? ""] ?? "Arrived").toLowerCase()}</span>
                  </div>
                  <div className="truncate text-[11px] text-zinc-500">
                    {t.how}{path && path !== "/" ? ` · landed on ${path}` : ""}
                  </div>
                </div>
                <span className="shrink-0 text-[11px] tabular-nums text-zinc-500" title={new Date(t.ts).toLocaleString()}>{timeAgo(t.ts)}</span>
              </li>
            );
          })}
        </ul>
      )}
    </Section>
  );
}
